// VAINE Widget

// Copyright (c) 2020, Pacific Northwest National Laboratories
// All rights reserved.

// Redistribution and use in source and binary forms, with or without
// modification, are permitted provided that the following conditions are met:

// * Redistributions of source code must retain the above copyright notice, this
//   list of conditions and the following disclaimer.

// * Redistributions in binary form must reproduce the above copyright notice,
//   this list of conditions and the following disclaimer in the documentation
//   and/or other materials provided with the distribution.

// * Neither the name of the copyright holder nor the names of its
//   contributors may be used to endorse or promote products derived from
//   this software without specific prior written permission.

// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
// AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
// DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
// FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
// DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
// SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
// CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
// OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
// OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

import React, { useRef, useEffect } from 'react';

import * as d3 from 'd3';
import crossfilter from 'crossfilter2';

import { CollapseSelectors } from './CollapseSelectors';

import { makeStyles } from '@material-ui/core/styles';

var useStyles = makeStyles(function (theme) {
  return {
    PCPcontainer: {
      marginLeft: 0,
      display: "flex",
      flexDirection: "column"
    }
  };
});

// Visualize covariates in a parallel coordinates plot
export var PCP = function PCP(_ref) {
  var data = _ref.data,
      regressions = _ref.regressions,
      _ref$ignore = _ref.ignore,
      ignore = _ref$ignore === undefined ? [] : _ref$ignore,
      selectedTreatment = _ref.selectedTreatment,
      selectedOutcome = _ref.selectedOutcome,
      validClusters = _ref.validClusters,
      _ref$deselected = _ref.deselected,
      deselected = _ref$deselected === undefined ? [] : _ref$deselected,
      _ref$clusterAppearanc = _ref.clusterAppearance,
      clusterAppearance = _ref$clusterAppearanc === undefined ? {} : _ref$clusterAppearanc,
      onDeselect = _ref.onDeselect;

  var classes = useStyles();
  var ref = useRef('pcp');

  var _React$useState = React.useState([]),
      dataset = _React$useState[0],
      setDataset = _React$useState[1];

  var _React$useState2 = React.useState({}),
      covariates = _React$useState2[0],
      setCovariates = _React$useState2[1];

  var _React$useState3 = React.useState([]),
      selectedCovariates = _React$useState3[0],
      setSelectedCovariates = _React$useState3[1];

  var _React$useState4 = React.useState({}),
      selectedData = _React$useState4[0],
      setSelectedData = _React$useState4[1];

  var _React$useState5 = React.useState({}),
      deselectedData = _React$useState5[0],
      setDeselectedData = _React$useState5[1];

  var _React$useState6 = React.useState({}),
      yExtents = _React$useState6[0],
      setYExtents = _React$useState6[1];

  var _React$useState7 = React.useState(false),
      unionSelect = _React$useState7[0],
      setUnionSelect = _React$useState7[1];

  // Get all included points


  useEffect(function () {
    var newDataset = [];
    for (var _iterator = Object.keys(regressions), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
      var _ref2;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref2 = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref2 = _i.value;
      }

      var r = _ref2;

      newDataset = newDataset.concat(regressions[r].included);
    }

    setDataset(newDataset);
  }, [regressions]);

  // Determine selected and deselected data
  useEffect(function () {
    var isDeselected = function isDeselected(d) {
      var cluster = d.cluster;
      if (!validClusters[cluster]) {
        return true;
      }

      var index = d.index;
      if (deselected.indexOf(index) > -1) {
        return true;
      }

      return false;
    };

    var newDeselectedData = dataset.filter(function (d) {
      return isDeselected(d);
    });
    var newSelectedData = dataset.filter(function (d) {
      return !isDeselected(d);
    });

    setDeselectedData(newDeselectedData);
    setSelectedData(newSelectedData);
  }, [deselected, dataset, validClusters]);

  // Determine all covariates available
  // Default to showing the first 5
  useEffect(function () {
    var originalVariables = data.columns;
    var allCovariates = [];

    for (var _iterator2 = originalVariables, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
      var _ref3;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref3 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref3 = _i2.value;
      }

      var v = _ref3;

      if (v === selectedTreatment || v === selectedOutcome) {
        continue;
      } else if (ignore.indexOf(v) > -1) {
        continue;
      } else {
        allCovariates.push(v);
      }
    }

    var newCovariates = {};
    var newSelectedCovariates = [];

    for (var i = 0; i < allCovariates.length; i++) {
      var c = allCovariates[i];
      if (i < 5) {
        newCovariates[c] = true;
        newSelectedCovariates.push(c);
      } else {
        newCovariates[c] = false;
      }
    }

    setCovariates(newCovariates);
    setSelectedCovariates(newSelectedCovariates);
  }, [data, selectedTreatment, selectedOutcome]);

  // Track brushed extents of each covariate
  useEffect(function () {
    var filterSelection = function filterSelection() {
      var newCF = crossfilter(dataset);
      var crossfilterDimensions = {};

      var currentSelection = [];

      var _loop = function _loop(c) {

        var dimensionExtent = yExtents[c];

        if (!dimensionExtent) {
          return 'continue';
        }
        var newDimension = newCF.dimension(function (d) {
          return d[c];
        });
        newDimension.filter(function (d) {
          return d >= dimensionExtent[0] && d <= dimensionExtent[1];
        });
        currentSelection = newDimension.top(Infinity);
      };

      for (var _iterator3 = selectedCovariates, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _iterator3[Symbol.iterator]();;) {
        var _ref4;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref4 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref4 = _i3.value;
        }

        var c = _ref4;

        var _ret = _loop(c);

        if (_ret === 'continue') continue;
      }

      return currentSelection;
    };

    var currentSelections = filterSelection();
    if (currentSelections.length === dataset.length) {
      onDeselect([], 'pcp', true);
      return;
    }
    var currentBrushedIndices = currentSelections.map(function (d) {
      return d.index;
    });

    var nonBrushed = dataset.filter(function (d) {
      return currentBrushedIndices.indexOf(d.index) === -1;
    });
    var nonBrushedIndices = nonBrushed.map(function (d) {
      return d.index;
    });

    onDeselect(nonBrushedIndices, 'pcp', unionSelect);
  }, [yExtents]);

  // Update covariates shown on user select
  var onSelectCovariate = function onSelectCovariate(val) {
    setCovariates(val);

    var newSelectedCovariates = [];
    for (var _iterator4 = Object.keys(val), _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _iterator4[Symbol.iterator]();;) {
      var _ref5;

      if (_isArray4) {
        if (_i4 >= _iterator4.length) break;
        _ref5 = _iterator4[_i4++];
      } else {
        _i4 = _iterator4.next();
        if (_i4.done) break;
        _ref5 = _i4.value;
      }

      var c = _ref5;

      if (val[c]) {
        newSelectedCovariates.push(c);
      }
    }

    setSelectedCovariates(newSelectedCovariates);
  };

  var layout = { "width": 615,
    "height": 280,
    "marginDefault": 60,
    "marginTop": 30,
    "marginBottom": 10 };

  useEffect(function () {
    var svgElement = d3.select(ref.current);

    svgElement.selectAll("rect.selection").attr("width", 16).attr("x", bandWidth - 8);

    var g = svgElement.select("#pcpSelected");
    var gAxis = svgElement.select("#pcpAxes");
    var gDeselected = svgElement.select("#pcpDeselected");

    var svgWidth = +svgElement.attr('width');
    var svgHeight = +svgElement.attr('height');

    var chartWidth = svgWidth - layout.marginDefault * 2;
    var chartHeight = svgHeight - (layout.marginTop + layout.marginBottom);

    var yScales = {};

    var _loop2 = function _loop2(m) {
      var metricScale = void 0;
      var domain = void 0;
      domain = d3.extent(dataset, function (d) {
        return d[m];
      });
      metricScale = d3.scaleLinear().range([chartHeight, 0]).domain(domain).nice();

      yScales[m] = metricScale.domain(domain);
    };

    for (var _iterator5 = selectedCovariates, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _iterator5[Symbol.iterator]();;) {
      var _ref6;

      if (_isArray5) {
        if (_i5 >= _iterator5.length) break;
        _ref6 = _iterator5[_i5++];
      } else {
        _i5 = _iterator5.next();
        if (_i5.done) break;
        _ref6 = _i5.value;
      }

      var m = _ref6;

      _loop2(m);
    }

    var xScale = d3.scalePoint().range([0, chartWidth]).domain(selectedCovariates);

    var maxTextLength = 15;

    var textData = [];

    // Divide long covariate names into multiple lines
    for (var _iterator6 = selectedCovariates, _isArray6 = Array.isArray(_iterator6), _i6 = 0, _iterator6 = _isArray6 ? _iterator6 : _iterator6[Symbol.iterator]();;) {
      var _ref7;

      if (_isArray6) {
        if (_i6 >= _iterator6.length) break;
        _ref7 = _iterator6[_i6++];
      } else {
        _i6 = _iterator6.next();
        if (_i6.done) break;
        _ref7 = _i6.value;
      }

      var m = _ref7;

      if (m.length < maxTextLength) {
        textData.push({ 'text': m, 'x': xScale(m), 'y': -9 });
      } else if (m.split(" ").length === 1) {
        textData.push({ 'text': m, 'x': xScale(m), 'y': -9 });
      } else {
        var splitCovariate = m.split(" ");

        var mergeSplits = [];
        var currentSplit = '';

        for (var _iterator7 = splitCovariate, _isArray7 = Array.isArray(_iterator7), _i7 = 0, _iterator7 = _isArray7 ? _iterator7 : _iterator7[Symbol.iterator]();;) {
          var _ref8;

          if (_isArray7) {
            if (_i7 >= _iterator7.length) break;
            _ref8 = _iterator7[_i7++];
          } else {
            _i7 = _iterator7.next();
            if (_i7.done) break;
            _ref8 = _i7.value;
          }

          var s = _ref8;

          if (s.length > maxTextLength) {
            mergeSplits.push(currentSplit);
            mergeSplits.push(s);
            currentSplit = '';
          } else {
            currentSplit = currentSplit + ' ' + s;
          }

          if (currentSplit.length > maxTextLength) {
            mergeSplits.push(currentSplit);
            currentSplit = '';
          }
        }

        if (currentSplit !== '') {
          mergeSplits.push(currentSplit);
        }

        var currentY = -9 * mergeSplits.length;

        for (var _iterator8 = mergeSplits, _isArray8 = Array.isArray(_iterator8), _i8 = 0, _iterator8 = _isArray8 ? _iterator8 : _iterator8[Symbol.iterator]();;) {
          var _ref9;

          if (_isArray8) {
            if (_i8 >= _iterator8.length) break;
            _ref9 = _iterator8[_i8++];
          } else {
            _i8 = _iterator8.next();
            if (_i8.done) break;
            _ref9 = _i8.value;
          }

          var ms = _ref9;

          textData.push({ 'text': ms, 'x': xScale(m), 'y': currentY });
          currentY = currentY + 9;
        }
      }
    }

    function pathConfirmed(d) {
      return d3.line()(selectedCovariates.map(function (p) {
        return [xScale(p), yScales[p](d[p])];
      }));
    }

    var colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(d3.map(dataset, function (d) {
      return d.cluster;
    }).keys().sort());

    var fillColor = function fillColor(d) {
      var cluster = d.cluster;
      if (!validClusters[cluster]) {
        return 'lightgray';
      }

      var index = d.index;
      if (deselected.indexOf(index) > -1) {
        return 'lightgray';
      }

      return clusterAppearance[cluster] ? clusterAppearance[cluster].color : 'lightgray';
    };

    var opacity = function opacity(d) {
      var cluster = d.cluster;
      if (!validClusters[cluster]) {
        return 0;
      }

      var index = d.index;
      if (deselected.indexOf(index) > -1) {
        return 0;
      }

      return 0.3;
    };

    gDeselected.selectAll(".deselectedPath").data(deselectedData).join("path").attr("class", "deselectedPath").attr("d", pathConfirmed).style("fill", "none").style("stroke", 'lightgray').style("opacity", 0.3);

    g.selectAll(".strokePath").data(selectedData).join("path").attr("class", "strokePath").attr("d", pathConfirmed).style("fill", "none").style("stroke", function (d) {
      return fillColor(d);
    }).style("opacity", function (d) {
      return opacity(d);
    });

    gAxis.selectAll(".myAxis").data(selectedCovariates).join("g").attr("class", "myAxis").attr("transform", function (d) {
      return "translate(" + xScale(d) + ")";
    }).each(function (d) {
      d3.select(this).call(d3.axisLeft().scale(yScales[d]));
    });

    g.selectAll(".myText").data(textData).join("text").attr("class", "myText").attr("transform", function (d) {
      return "translate(" + d.x + ")";
    }).attr("y", function (d) {
      return d.y;
    }).text(function (d) {
      return d.text;
    }).style("text-anchor", "middle").style("fill", "black").style("font-size", 8).style("font-family", '"Roboto", "Helvetica", "Arial", sans-serif');

    d3.selectAll(".tick>text").style("font-size", 8);

    var bandWidth = xScale.step() / 2;

    // Set brush size to 16px width
    var brushstart = function brushstart() {
      svgElement.selectAll("rect.selection").attr("width", 16).attr("x", bandWidth - 8);
      d3.event.sourceEvent.stopPropagation();
    };

    var brush = function brush(d) {
      svgElement.selectAll("rect.selection").attr("width", 16).attr("x", bandWidth - 8);
    };

    // Determine which points are selected
    // Update deselected points
    // If `command` (meta) key is held down, perform union select
    var brushend = function brushend(d) {
      svgElement.selectAll("rect.selection").attr("width", 16).attr("x", bandWidth - 8);
      d3.event.sourceEvent.stopPropagation();

      var newYExtents = JSON.parse(JSON.stringify(yExtents));

      if (d3.event.selection === null) {
        newYExtents[d] = d3.extent(dataset, function (datum) {
          return datum[d];
        });
      } else {
        var scale = yScales[d];

        var covariateUpperBound = scale.invert(d3.event.selection[0]);
        var covariateLowerBound = scale.invert(d3.event.selection[1]);

        newYExtents[d] = [covariateLowerBound, covariateUpperBound];
      }

      setUnionSelect(d3.event.sourceEvent.metaKey);
      setYExtents(newYExtents);
    };

    // Attach brush for each covariate
    gAxis.selectAll(".brush").data(selectedCovariates).join("g").attr("class", "brush").attr("transform", function (d) {
      return "translate(" + (xScale(d) - bandWidth) + ")";
    }).each(function (d) {
      d3.select(this).call(yScales[d].brush = d3.brushY().on("start", brushstart).on("brush", function () {
        return brush(d);
      }).on("end", function () {
        return brushend(d);
      }));

      svgElement.selectAll("rect.selection").attr("width", 16).attr("x", bandWidth - 8);
    });
  }, [dataset, deselected, selectedData, deselectedData, covariates, selectedCovariates, validClusters]);

  return React.createElement(
    'div',
    { className: classes.PCPcontainer },
    React.createElement(
      'div',
      { className: classes.selectorContainer },
      React.createElement(CollapseSelectors, { options: covariates,
        sortedClusters: Object.keys(covariates),
        showColor: false,
        onSelect: onSelectCovariate,
        title: 'Covariates' })
    ),
    React.createElement(
      'svg',
      { width: layout.width, height: layout.height, ref: ref, id: 'pcp' },
      React.createElement('g', { transform: "translate(" + layout.marginDefault + "," + layout.marginTop + ")", id: 'pcpDeselected' }),
      React.createElement('g', { transform: "translate(" + layout.marginDefault + "," + layout.marginTop + ")", id: 'pcpSelected' }),
      React.createElement('g', { transform: "translate(" + layout.marginDefault + "," + layout.marginTop + ")", id: 'pcpAxes' })
    )
  );
};


// WEBPACK FOOTER //
// ./es/PCP.js